import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';

const DevOpsAutomation = () => {
  const navigate = useNavigate();

  const handleContactClick = () => {
    navigate('/#contact');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">DevOps & Automation</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Streamline your development lifecycle with cutting-edge DevOps practices and intelligent automation solutions.
          </p>
        </div>

        {/* Services Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">DevOps Services</h2>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <i className="fas fa-infinity text-white text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">CI/CD Pipeline Setup</h3>
                  <p className="text-gray-600">Automated build, test, and deployment pipelines for faster releases.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <i className="fas fa-cubes text-white text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Containerization</h3>
                  <p className="text-gray-600">Docker and Kubernetes implementation for scalable deployments.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <i className="fas fa-code-branch text-white text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Infrastructure as Code</h3>
                  <p className="text-gray-600">Terraform and Ansible for reproducible infrastructure management.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <i className="fas fa-chart-line text-white text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Monitoring & Observability</h3>
                  <p className="text-gray-600">Comprehensive monitoring with Prometheus, Grafana, and ELK stack.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Automation Solutions</h2>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                    <i className="fas fa-robot text-white text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Process Automation</h3>
                  <p className="text-gray-600">Automate repetitive tasks and business processes for efficiency.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                    <i className="fas fa-vial text-white text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Automated Testing</h3>
                  <p className="text-gray-600">Unit, integration, and end-to-end testing automation.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                    <i className="fas fa-shield-alt text-white text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Security Automation</h3>
                  <p className="text-gray-600">Automated security scanning and compliance checks.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                    <i className="fas fa-database text-white text-sm"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Backup Automation</h3>
                  <p className="text-gray-600">Automated backup and disaster recovery solutions.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tools & Technologies */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Tools & Technologies</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
            <div className="text-center p-4 border border-gray-200 rounded-lg">
              <i className="fab fa-jenkins text-4xl text-blue-600 mb-3"></i>
              <h3 className="font-semibold text-gray-900">Jenkins</h3>
            </div>
            <div className="text-center p-4 border border-gray-200 rounded-lg">
              <i className="fab fa-docker text-4xl text-blue-500 mb-3"></i>
              <h3 className="font-semibold text-gray-900">Docker</h3>
            </div>
            <div className="text-center p-4 border border-gray-200 rounded-lg">
              <i className="fas fa-dharmachakra text-4xl text-blue-700 mb-3"></i>
              <h3 className="font-semibold text-gray-900">Kubernetes</h3>
            </div>
            <div className="text-center p-4 border border-gray-200 rounded-lg">
              <i className="fab fa-aws text-4xl text-orange-500 mb-3"></i>
              <h3 className="font-semibold text-gray-900">AWS</h3>
            </div>
            <div className="text-center p-4 border border-gray-200 rounded-lg">
              <i className="fas fa-layer-group text-4xl text-purple-600 mb-3"></i>
              <h3 className="font-semibold text-gray-900">Terraform</h3>
            </div>
            <div className="text-center p-4 border border-gray-200 rounded-lg">
              <i className="fab fa-gitlab text-4xl text-orange-600 mb-3"></i>
              <h3 className="font-semibold text-gray-900">GitLab CI</h3>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-8 text-white text-center">
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-rocket text-2xl"></i>
            </div>
            <h3 className="text-xl font-bold mb-4">Faster Delivery</h3>
            <p className="opacity-90">Reduce deployment time from hours to minutes with automated pipelines.</p>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-8 text-white text-center">
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-bug text-2xl"></i>
            </div>
            <h3 className="text-xl font-bold mb-4">Fewer Errors</h3>
            <p className="opacity-90">Automated testing and deployment reduce human errors significantly.</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-8 text-white text-center">
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-chart-line text-2xl"></i>
            </div>
            <h3 className="text-xl font-bold mb-4">Better Scalability</h3>
            <p className="opacity-90">Infrastructure that scales automatically based on demand.</p>
          </div>
        </div>

        {/* Implementation Process */}
        <div className="bg-gray-900 rounded-lg shadow-lg p-8 text-white mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center">Implementation Process</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold">1</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Assessment</h3>
              <p className="text-sm opacity-90">Analyze current processes and infrastructure</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold">2</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Strategy</h3>
              <p className="text-sm opacity-90">Design DevOps strategy and automation roadmap</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold">3</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Implementation</h3>
              <p className="text-sm opacity-90">Deploy tools and establish automated workflows</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold">4</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Optimization</h3>
              <p className="text-sm opacity-90">Monitor, measure, and continuously improve</p>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Expected Results</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-primary mb-2">80%</div>
              <p className="text-gray-600">Faster Deployments</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-green-600 mb-2">60%</div>
              <p className="text-gray-600">Reduced Errors</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-purple-600 mb-2">99.9%</div>
              <p className="text-gray-600">System Uptime</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-orange-600 mb-2">50%</div>
              <p className="text-gray-600">Cost Savings</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Accelerate Your Development?</h2>
          <p className="text-xl text-gray-600 mb-8">Transform your development lifecycle with our DevOps and automation expertise.</p>
          <button 
            onClick={handleContactClick}
            className="bg-primary hover:bg-blue-700 text-white px-8 py-3 rounded-lg text-lg font-medium transition-colors"
          >
            Get Started Today
          </button>
        </div>
      </div>
    </div>
  );
};

export default DevOpsAutomation;
